import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  debounceTime,
  catchError,
  distinctUntilChanged,
  EMPTY,
  expand,
  last,
  Subject,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';

import { ApiCallState } from '../../shared/api-call-state';
import { ApiResultComponent } from '../../shared/api-result.component';
import { dateTimeInputValue } from '../../shared/date-utils';
import {
  TrackerFilter,
  TrackerLog,
  TrackerQuery,
  TrackerResponse,
  TrackerService,
} from './tracker.service';

interface FilterOption {
  label: string;
  group: string;
  checked: boolean;
}

interface DisplayLog extends TrackerLog {
  day: string;
  month: string;
  time: string;
  displayMessage: string;
  duplicateCount: number;
}

interface TimelineGroup {
  key: string;
  date: string;
  logs: DisplayLog[];
}

const FILTERS: readonly FilterOption[] = [
  { label: 'Delivery Options', group: 'deliveryoptions', checked: true },
  { label: 'Access Request DELIVERY', group: 'accessRequest', checked: true },
  { label: 'Access Request PICKUP', group: 'accessRequest', checked: true },
  { label: 'Access Key', group: 'accessKey', checked: true },
  { label: 'Access Event', group: 'accessEvent', checked: true },
  { label: 'Access Deviation', group: 'accessDeviation', checked: true },
  { label: 'Lockedspace Event', group: 'lockedspaceEvent', checked: true },
  { label: 'Revoke', group: 'revoke', checked: true },
  { label: 'Step Up Recipient Instructions', group: 'checkPermission', checked: true },
  { label: 'Expiry Trigger', group: 'triggerExpiryRevoke', checked: true },
  { label: 'Change Compartment', group: 'changeCompartment', checked: true },
];

const GROUP_NAMES: ReadonlyArray<{ name: string; operations: readonly string[] }> = [
  { name: 'Delivery Options', operations: ['deliveryoptions'] },
  {
    name: 'Access Booking/Request',
    operations: ['accessRequest', 'accessRequestCallback', 'accessRequestSQS'],
  },
  {
    name: 'Access Key Request',
    operations: ['accessKey', 'accessKeyCallback', 'accessKeySQS', 'accessKeyGet'],
  },
  { name: 'Access Event/Deviation', operations: ['accessEvent', 'accessDeviation'] },
  { name: 'Lockedspace Event', operations: ['lockedspaceEvent'] },
  { name: 'Revoke', operations: ['revoke', 'revokeCallback'] },
  { name: 'Step Up', operations: ['checkPermission', 'itemValidator', 'getInstructions'] },
  { name: 'Expiry Trigger', operations: ['triggerExpiryRevoke'] },
  { name: 'Change Compartment', operations: ['changeCompartment'] },
];

@Component({
  selector: 'app-tracker',
  imports: [FormsModule, ApiResultComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tracker.component.html',
})
export class TrackerComponent implements OnInit {
  private readonly tracker = inject(TrackerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly itemChanges = new Subject<string>();

  protected readonly call = new ApiCallState();
  protected readonly filters = signal<readonly FilterOption[]>(FILTERS.map((item) => ({ ...item })));
  protected readonly groups = signal<readonly TimelineGroup[]>([]);
  protected readonly status = signal('Ready');
  protected readonly country = signal('');
  protected readonly loadingDates = signal(false);
  protected readonly endpoint = computed(() =>
    this.production
      ? 'Production tracker /queryDB'
      : 'Acceptance-test tracker /queryDB',
  );

  protected production = true;
  protected itemId = '';
  protected startTime = startOfTodayInput();
  protected endTime = dateTimeInputValue(new Date());
  protected readonly maxTime = dateTimeInputValue(new Date());

  constructor() {
    this.itemChanges
      .pipe(
        debounceTime(180),
        distinctUntilChanged(),
        switchMap((itemId) => {
          if (!itemId.trim()) {
            return EMPTY;
          }
          this.loadingDates.set(true);
          return this.tracker.queryDates(itemId.trim(), this.production).pipe(
            catchError(() => {
              this.loadingDates.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (dates) => {
          this.applyDates(dates.startTime, dates.endTime);
          this.loadingDates.set(false);
        },
      });
  }

  ngOnInit(): void {
    const itemId = new URLSearchParams(location.search).get('itemId');
    if (itemId) {
      this.itemId = itemId;
      this.loadingDates.set(true);
      this.tracker
        .queryDates(itemId, this.production)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (dates) => {
            this.applyDates(dates.startTime, dates.endTime);
            this.loadingDates.set(false);
            this.submit();
          },
          error: () => {
            this.loadingDates.set(false);
            this.submit();
          },
        });
    }
  }

  protected itemChanged(value: string): void {
    this.itemChanges.next(value);
  }

  protected stageChanged(): void {
    this.call.clear();
    this.groups.set([]);
    this.status.set('Ready');
    if (this.itemId) {
      this.itemChanges.next(this.itemId);
    }
  }

  protected toggleFilter(index: number, checked: boolean): void {
    this.filters.update((filters) =>
      filters.map((filter, itemIndex) =>
        itemIndex === index ? { ...filter, checked } : filter,
      ),
    );
  }

  protected setAllFilters(checked: boolean): void {
    this.filters.update((filters) => filters.map((filter) => ({ ...filter, checked })));
  }

  protected submit(): void {
    const payload: TrackerQuery = {
      itemId: this.itemId.trim(),
      startTime: new Date(this.startTime).toISOString(),
      endTime: new Date(this.endTime).toISOString(),
      requests: buildRequestFilters(this.filters()),
    };

    this.groups.set([]);
    this.country.set('');
    this.status.set('Running');
    this.call.start(payload);

    this.tracker
      .queryDatabase(payload, this.production)
      .pipe(
        expand((response) => {
          if (response.status !== 'PENDING' || !response.queryId) {
            return EMPTY;
          }
          return timer(750).pipe(
            switchMap(() => this.tracker.queryResult(response.queryId!, this.production)),
          );
        }),
        takeWhile((response) => response.status === 'PENDING', true),
        last(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => this.handleResponse(response),
        error: (error: unknown) => {
          this.status.set('Query failed');
          this.call.fail(error);
        },
      });
  }

  private handleResponse(response: TrackerResponse): void {
    this.status.set(response.status);
    const logs = Array.isArray(response.data) ? response.data : [];
    const groups = groupLogs(logs);
    this.groups.set(groups);
    this.country.set(String(groups[0]?.logs[0]?.country ?? ''));
    this.call.succeed({ status: response.status, logCount: logs.length });
  }

  private applyDates(startTime?: string, endTime?: string): void {
    if (startTime) {
      this.startTime = dateTimeInputValue(new Date(startTime));
    }
    if (endTime) {
      this.endTime = dateTimeInputValue(new Date(endTime));
    } else {
      this.endTime = dateTimeInputValue(new Date());
    }
  }
}

export function buildRequestFilters(options: readonly FilterOption[]): TrackerFilter[] {
  const result: TrackerFilter[] = [];

  for (const option of options.filter(({ checked }) => checked)) {
    const groups = [option.group];
    if (option.label.includes('Access Request')) {
      groups.push('accessRequestSQS', 'accessRequestCallback');
    } else if (option.label.includes('Access Key')) {
      groups.push('accessKeySQS', 'accessKeyCallback', 'accessKeyGet');
    } else if (option.label.includes('Revoke')) {
      groups.push('revokeCallback');
    } else if (option.label.includes('Step Up')) {
      groups.push('itemValidator', 'getInstructions');
    }

    const accessType = option.label.includes('DELIVERY')
      ? 'DELIVERY'
      : option.label.includes('PICKUP')
        ? 'PICKUP'
        : undefined;

    for (const group of groups) {
      result.push({
        group,
        accessType: group === 'accessRequestSQS' && accessType === 'PICKUP' ? '' : accessType,
      });
    }
  }

  return result;
}

export function groupLogs(logs: readonly TrackerLog[]): TimelineGroup[] {
  const duplicates = new Map<string, DisplayLog>();

  for (const log of logs) {
    const message = String(log.message ?? '');
    if (message.includes('Swipbox parcel')) {
      continue;
    }

    const key = `${String(log.requestid ?? '')}${message}`;
    const existing = duplicates.get(key);
    if (existing) {
      existing.duplicateCount += 1;
      continue;
    }

    const parts = stockholmParts(String(log.timestamp ?? ''));
    duplicates.set(key, {
      ...log,
      message,
      day: parts.day,
      month: parts.month,
      time: parts.time,
      displayMessage: rephraseMessage(log, message),
      duplicateCount: 1,
    });
  }

  const groups: TimelineGroup[] = [];
  for (const log of duplicates.values()) {
    const operation = String(log.operation ?? log.loggroup ?? 'Other');
    const groupName =
      GROUP_NAMES.find(({ operations }) => operations.includes(operation))?.name ?? operation;
    const date = `${log.month}-${log.day}`;
    const previous = groups.at(-1);
    if (previous?.key === groupName && previous.date === date) {
      previous.logs.push(log);
    } else {
      groups.push({ key: groupName, date, logs: [log] });
    }
  }

  return groups;
}

function stockholmParts(value: string): { day: string; month: string; time: string } {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { day: '--', month: '---', time: value };
  }

  const day = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    timeZone: 'Europe/Stockholm',
  }).format(date);
  const month = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'Europe/Stockholm',
  }).format(date);
  const time = new Intl.DateTimeFormat('en-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'Europe/Stockholm',
  }).format(date);
  return { day, month, time: `${time} CET` };
}

function rephraseMessage(log: TrackerLog, message: string): string {
  if (message === 'Item to process...' || message === 'Incoming Event') {
    return String(log.operation ?? '').includes('Callback')
      ? 'Callback process started!'
      : 'Process started!';
  }
  if (message.includes('"statuscode":"202"')) {
    return 'Waiting for callback...';
  }
  if (message.includes('Trigger Expiry')) {
    return 'Parcel expired!';
  }
  if (message.includes('Number of lockedspaces')) {
    return `${message}: ${String(log['lockedspaceslength'] ?? '')}`;
  }
  return message;
}

function startOfTodayInput(): string {
  const value = new Date();
  value.setHours(0, 0, 0, 0);
  return dateTimeInputValue(value);
}